/**
 * @fileoverview The garden's plants, read once and shared, and the ways to
 * add, change, archive and restore one.
 *
 * Reads GET /plants (api/plants) under [PLANTS_QUERY_KEY, userId, zip ||
 * 'none', language], with the app's default staleness (five minutes); the ZIP
 * is in the key because the server merges each crop's temperatures with that
 * climate, and the language because each plant's names come in it. The ZIP is the signed-in account's own, sent only in their own
 * garden; in a shared garden api/plants leaves it off, so the server uses
 * the owner's (gardenMembers.gardenZip). Whether the garden has a postcode
 * Grow can plan from (missingZipCode, the Plants page's banner) is the
 * server's answer (useGardenPostcode, the garden place: the owner's in a
 * shared garden, never the member's own; audit A29). Writes go through api/plantCatalogue, then refetch the list
 * (awaited, the gardener is about to look at it) and invalidate, without
 * waiting, Grow's advice (the outlook, and Today's brief read again:
 * lib/briefFreshness gardenWritten, D56: the plants brief, which also says
 * whether each card offers About this plant, W24 Job A), the archived list and
 * the beds (refreshGarden). Deleting a plant for good is not here
 * (ArchivedPlants and the plant form call deletePlantForever).
 *
 * Other screens change the cached list in place through
 * queryClient.setQueriesData({ queryKey: ['plants'] }) (notes, the public
 * switch), so the prefix key matters: keep PLANTS_QUERY_KEY first.
 *
 * A write that fails on our side (a 500, no connection) answers
 * `{ success: false, error }` with the standard sentence (D31,
 * lib/serverFailure), reported to Sentry; any other failure leaves `error`
 * empty for the caller to word, as before.
 *
 * The list on demand (W24 Job C): a page that does not draw the list (Today)
 * mounts the provider with `listOnDemand`. There GET /plants is not read until
 * something reads `plants`, `isLoading` or `error` from the context (an open
 * sheet that draws the garden: Check and save's piles, the plant form's own
 * plants); the writes (addPlant and the rest) work without it, and their
 * refetch reads nothing while the list was never wanted. So a to-do marked on
 * Today, which invalidates the plants, downloads nothing there. Every other
 * page reads the list at once, as before.
 *
 * Mounted per route by App around the pages that read the plants; read with
 * shared/hooks/usePlantsContext.
 *
 * @module PlantsContext
 */

import { markPlantChanged } from '@/shared/lib/plantChanged';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { getPlants, getSinglePlant } from '../api/plants';
import type { Plant } from '@/shared/types/plant';
import { PlantsContext, type PlantsContextType } from './PlantsContextDefinition';
import { debugLog } from '../../config/config';
import { useAuth } from '../hooks/useAuth';
import { useGardenPostcode } from '../hooks/useGardenPostcode';
import { useTranslation } from '../hooks/useTranslation';
import { mergePlant } from '@/shared/api/plantMerge';
import { createPlant, updatePlant, setPlantStatus, PLANT_STATUS, type PlantWritePayload } from '@/shared/api/plantCatalogue';
import { ARCHIVED_PLANTS_QUERY_KEY, LOCATIONS_QUERY_KEY, PLANTS_QUERY_KEY } from '@/config/tunables/ui';
import { PLANT_OUTLOOK_QUERY_KEY } from '@/config/tunables/shared';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { gardenWritten } from '@/shared/lib/briefFreshness';


/** What PlantsProvider is given. */
interface PlantsProviderProps {
  /** The page inside it. */
  children: ReactNode;
  /**
   * The page draws no plant list (Today): GET /plants is read only once something reads the list from the context.
   * Left out, the list is read at once.
   */
  listOnDemand?: boolean;
}

/**
 * Gives the page inside it the garden's plants and the ways to add, change, archive, restore and merge them.
 *
 * @param props - PlantsProviderProps.
 * @returns The provider around the page.
 */
export function PlantsProvider({ children, listOnDemand = false }: PlantsProviderProps): JSX.Element {

  /** The signed-in account (AuthContext): its user id and postcode. */
  const { mysqlUser } = useAuth();
  /** The app's language, which the plants' names come in. */
  const { i18n: { language } } = useTranslation();
  /** The signed-in account's user id; undefined before sign-in. */
  const userId = mysqlUser?.user_id;
  /** The account's own postcode, or '' (sent only in its own garden: api/plants). */
  const zip = mysqlUser?.postal_code?.trim() || '';
  /** The garden place, as the server says it (useGardenPostcode). */
  const place = useGardenPostcode();
  /** No postcode Grow can plan from, as the server says it: once it has said, and only then. */
  const missingZipCode = !!userId && place.known && !place.usable;

  /** The app's query client, for the invalidations after a write. */
  const queryClient = useQueryClient();

  /**
   * Bring every view of the garden back in step after a write.
   *
   * The outlook is fetched under its own key — one request covering every
   * plant — and its key does not mention which plants exist, so adding one
   * left a fresh cache entry that simply had no row for it. The card fell back
   * to its own temperature summary until the next full page load.
   *
   * Locations for the same reason, one step further out: a bed carries its own
   * list of what is planted in it, and a plant can be moved between beds from
   * the edit form — which the locations page can now open. Without this, moving
   * a plant out of the Greenhouse left the Greenhouse still listing it.
   *
   * Not the plant list itself. Every caller here already awaits refetch() on
   * it, and adding the invalidation as well meant two sequential fetches of the
   * whole garden per write — visible, because the grid shows "Loading..." while
   * it happens.
   *
   * Started, not awaited. invalidateQueries resolves only once the refetches
   * it triggers have settled, and one of these is the outlook — which the
   * server drops from its cache on every write, so the refetch can land on a
   * cold assembly that takes a good ten seconds to build. Awaiting all three in
   * a row held the Save button on "Saving..." for the whole of it, for work
   * whose only purpose is to refresh things behind the dialog. They are fired
   * here and land when they land; the plant list, which the gardener is about
   * to look at, is still awaited by the caller.
   */
  const refreshGarden = useCallback(async () => {
    // Grow's advice afresh: the outlook, and Today's brief read again (a plant moved to another bed changes both beds' jobs; D56).
    gardenWritten(queryClient);
    void queryClient.invalidateQueries({ queryKey: [ARCHIVED_PLANTS_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
  }, [queryClient]);

  /*
   * Whether the list is wanted: at once, unless the page said listOnDemand; then from the first read of it (wantList).
   * Once wanted it stays wanted while the page is open.
   */
  const [listWanted, setListWanted] = useState(!listOnDemand);
  /** Set when something read the list, before the state follows. */
  const wantedRef = useRef(!listOnDemand);
  /** True while the provider is on the page: the state is changed only then (never during a render, never on a server render). */
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    // A read made during the first render, before the provider was on the page.
    if (wantedRef.current) setListWanted(true);
    return () => { mountedRef.current = false; };
  }, []);
  /** Something read the list: it is read from the server, just after the render that asked for it. */
  const wantList = useCallback((): void => {
    if (wantedRef.current) return;
    wantedRef.current = true;
    if (mountedRef.current) queueMicrotask(() => { if (mountedRef.current) setListWanted(true); });
  }, []);

  /*
   * The garden's plants. Without a ZIP they load without climate-aware crop
   * temperatures; with no account yet, nothing is asked; on a listOnDemand page, not until it is wanted (a refetch
   * or an invalidation reads nothing either: React Query does not fetch a query that is switched off).
   */
  const { data: plants, isPending, error, refetch: refetchQuery } = useQuery({
    // The language is in the key: the plants' names come in it (services/grow/plantNaming), so a switch reads them again.
    queryKey: [PLANTS_QUERY_KEY, userId, zip || 'none', language],
    queryFn: () => (userId ? getPlants(zip || undefined, language) : Promise.resolve([])),
    enabled: !!userId && listWanted,
  });

  /**
   * Reads the list again, only when it is wanted: React Query's refetch fetches even a query that is switched off, so
   * a write on a listOnDemand page that never read the list (Today's add form) downloads nothing.
   *
   * @returns Done once the list is read again, or at once when it is not wanted.
   */
  const refetch = useCallback(async (): Promise<void> => {
    if (wantedRef.current) await refetchQuery();
  }, [refetchQuery]);

  /*
   * Pending, not fetching.
   *
   * PlantGrid returns a bare "Loading..." for this, so isFetching meant every
   * background refetch tore the whole garden down and built it again — and
   * with it every card's fold state and every photo carousel's position. Save
   * one plant and every card you had opened closed itself. isPending is true
   * only while there is nothing to show yet, which is the question the grid
   * is actually asking.
   */
  /** Nothing to show yet (see above). */
  const isLoading = !!userId && isPending;

  /**
   * Fetches and updates a single plant by its ID
   *
   * @function
   * @async
   * @param {string} plantObjId - The unique identifier of the plant to update
   * @returns {Promise<void>} A promise that resolves when the plant is updated
   * @throws {Error} If the plant fetch operation fails
   */
  const updateSinglePlant = useCallback(async (plantObjId: string): Promise<void> => {
    /*
     * The one plant, fetched and written into the garden in place.
     *
     * A photo changes more than the photo list the card already holds: the
     * server moves the plant's stage and dates along with it, and the card,
     * the tile and the row all read those from the garden. This used to fetch
     * the plant, throw the answer away and refetch the whole garden, and
     * nothing called it after an upload at all, so a new photograph left the
     * rest of the plant as it was until the page was reloaded.
     */
    try {
      const fresh = await getSinglePlant(plantObjId, zip || undefined, language);
      // B102: her own save; the Plants page keeps this plant where it was drawn.
      markPlantChanged(plantObjId);
      queryClient.setQueriesData<Plant[]>(
        { queryKey: [PLANTS_QUERY_KEY] },
        (garden) => (Array.isArray(garden)
          ? garden.map((plant) => (String(plant.plant_obj_id) === String(plantObjId) ? { ...plant, ...fresh } : plant))
          : garden)
      );
      // The outlook reads the stage; started, not awaited, as refreshGarden does.
      void queryClient.invalidateQueries({ queryKey: [PLANT_OUTLOOK_QUERY_KEY] });
      // A bed lists what is in it, and a stage can move a plant in or out of the Seed vault (D69): the beds are read again too.
      void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
    } catch (error) {
      debugLog('PlantsContext', '[updateSinglePlant] Error:', error);
      throw error;
    }
  }, [queryClient, zip, language]);

  /**
   * Adds a new plant to the system.
   *
   * Answers as soon as the plant is saved (B73): the plant list is read again in the background, not awaited.
   *
   * Failures carry the server's own message, or nothing.
   *
   * These used to substitute English — "Unknown error", "Failed to add plant" —
   * for a missing message, which put untranslated strings on a Spanish screen
   * and, worse, made every `result.error ?? t('...')` at a call site
   * unreachable: the fallback could never fire because error was never empty.
   * Undefined lets the caller say it in the reader's language. A failure on
   * our side is the exception: the standard sentence (serverFailureText),
   * which the caller shows in place of its own "could not save".
   *
   * @param payload - The plant's variety and bed (PlantWritePayload).
   * @returns success, the new plant's plant_obj_id (on
   *   success), and error: the server's message, the standard sentence, or
   *   undefined.
   */
  const addPlant = useCallback(async (payload: PlantWritePayload) => {
    try {
      debugLog('PlantsContext', '[addPlant] Starting with payload:', payload);

      if (!userId) {
        throw new Error('Cannot add plant: No authenticated user');
      }

      // Stage, date and transplant flag are passed on whole (they used to be
      // dropped here), though the backend now keeps only the variety and the
      // bed: a plant's stage is its stage history (api/stageMarks).
      const result = await createPlant(payload);
      if (result.success) {
        debugLog('PlantsContext', '[addPlant] Success, plant_obj_id:', result.plant_obj_id);
        // B73: the plant list is read again behind the form, not before it closes; the new plant shows when it lands.
        void refetch();
        await refreshGarden();
        return {
          success: true,
          plant_obj_id: result.plant_obj_id as string | number,
        };
      }
      return { success: false, error: result.message || undefined };
    } catch (error) {
      // A failure on our side is reported by serverFailureText; anything else is logged to Sentry as before.
      const words = serverFailureText(error, 'addPlant');
      if (!words) console.error('Error adding plant:', error);
      return { success: false, error: words ?? undefined };
    }
  }, [refetch, refreshGarden, userId]);

  /**
   * Changes a plant already in the garden.
   *
   * Answers as soon as the change is saved (B73); the garden is read again in the background, not awaited.
   *
   * Refetches rather than patching in place: an edit can move a plant to a new
   * location or variety, which changes the outlook advice and the care text
   * hanging off it, and those come from the server.
   *
   * @param plantObjId - The plant.
   * @param payload - Its variety (or crop and typeUnsure), bed and status (updatePlant sends only those, as a patch).
   * @returns Success with the plant's new twins (D66) when the server named some, or the server's message.
   */
  const editPlant = useCallback(async (plantObjId: number, payload: PlantWritePayload) => {
    try {
      const result = await updatePlant(plantObjId, payload);
      if (result.success) {
        // B73: answered once saved; the plant list is read again behind the form, which closes at once.
        void refetch();
        await refreshGarden();
        return { success: true, twins: result.twins };
      }
      return { success: false, error: result.message || undefined };
    } catch (error) {
      // A failure on our side is reported by serverFailureText; anything else is logged to Sentry as before.
      const words = serverFailureText(error, 'editPlant');
      if (!words) console.error('Error updating plant:', error);
      return { success: false, error: words ?? undefined };
    }
  }, [refetch, refreshGarden]);

  /**
   * Takes a plant out of the garden without destroying it.
   *
   * The list is refetched rather than filtered locally, because the server
   * decides what counts as still in the garden.
   *
   * @param plantObjId - The plant.
   * @returns Success, or the server's message.
   */
  const archivePlant = useCallback(async (plantObjId: number) => {
    try {
      const result = await setPlantStatus(plantObjId, PLANT_STATUS.ARCHIVED);
      if (result.success) {
        await refetch();
        await refreshGarden();
        return { success: true };
      }
      return { success: false, error: result.message || undefined };
    } catch (error) {
      // A failure on our side is reported by serverFailureText; anything else is logged to Sentry as before.
      const words = serverFailureText(error, 'archivePlant');
      if (!words) console.error('Error archiving plant:', error);
      return { success: false, error: words ?? undefined };
    }
  }, [refetch, refreshGarden]);

  /**
   * Puts an archived plant back in the garden.
   *
   * The mirror of archivePlant, and it had to become one. Archiving went
   * through here and brought the outlook back with it; restoring was written
   * out twice at its call sites, and neither copy mentioned the outlook. So a
   * plant taken out of the archive reappeared with no forecast on its card, no
   * part in the alert count and nothing in the bar above the garden — the
   * advice was still the advice for a garden it had not been in, and stayed
   * that way until the page was reloaded.
   *
   * Both directions are one call now, so the next thing that needs refreshing
   * is added once rather than remembered twice.
   *
   * @param plantObjId - The plant.
   * @returns Success, or the server's message.
   */
  const restorePlant = useCallback(async (plantObjId: number) => {
    try {
      const result = await setPlantStatus(plantObjId, PLANT_STATUS.ACTIVE);
      if (result.success) {
        await refetch();
        await refreshGarden();
        return { success: true };
      }
      return { success: false, error: result.message || undefined };
    } catch (error) {
      // A failure on our side is reported by serverFailureText; anything else is logged to Sentry as before.
      const words = serverFailureText(error, 'restorePlant');
      if (!words) console.error('Error restoring plant:', error);
      return { success: false, error: words ?? undefined };
    }
  }, [refetch, refreshGarden]);

  /**
   * Merges a plant into another (D66), then reads the garden again as archiving does.
   *
   * Unlike the other writes this one throws: the merge sheet words a refusal (the server's own
   * sentence) and a failure on our side (the standard sentence) itself, from the error.
   *
   * @param goneId - The plant that goes.
   * @param keepId - The plant that stays.
   * @param varietyId - The variety the kept plant has afterwards.
   * @returns The server's answer (the kept and merged ids and the counts moved).
   */
  const mergePlantInto = useCallback(async (goneId: number, keepId: number, varietyId: number) => {
    const result = await mergePlant(goneId, keepId, varietyId);
    await refetch();
    await refreshGarden();
    return result;
  }, [refetch, refreshGarden]);

  /**
   * What the context holds. One object per change, not per render: the whole garden reads this, and
   * a fresh object every render re-rendered every reader with it. On a listOnDemand page the list's three fields are
   * read through wantList, so reading them is what asks for the list; the writes do not.
   */
  const value = useMemo((): PlantsContextType => {
    const actions = {
      missingZipCode,
      refetchPlants: refetch,
      updateSinglePlant,
      addPlant,
      restorePlant,
      editPlant,
      archivePlant,
      mergePlantInto,
    };
    if (!listOnDemand) return { ...actions, plants, isLoading, error: error as Error | null };
    return {
      ...actions,
      /** The list, asked for by this read. */
      get plants() { wantList(); return plants; },
      /** Nothing to show yet, asked for by this read. */
      get isLoading() { wantList(); return isLoading; },
      /** The read's failure, asked for by this read. */
      get error() { wantList(); return error as Error | null; },
    };
  }, [listOnDemand, wantList, plants, isLoading, missingZipCode, error, refetch, updateSinglePlant, addPlant, restorePlant, editPlant, archivePlant, mergePlantInto]);

  return (
    <PlantsContext.Provider value={value}>
      {children}
    </PlantsContext.Provider>
  );
}